import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import config from 'constants/apiConfig';
import {
  selectGroupGuid,
  selectUser,
  useAppSelector,
} from 'slices/commonSelectors';

import { useIncident } from './incidentHooks';

export type IcsFormInputs = Record<string, unknown>;

type FormInputsResponse = { formname: string; inputs: IcsFormInputs };

export const icsFormInputsKeys = {
  all: ['icsFormInputs'] as const,
  one: (
    incidentId: string | undefined,
    formname: string,
    periodNumber: number | undefined,
    data: Record<string, unknown> | undefined
  ) => [...icsFormInputsKeys.all, incidentId, formname, periodNumber, data],
};

/**
 * The values a Coast Guard form would print.
 *
 * Read-only document cards show what the exported PDF will say, so they read
 * from the exporter's own fetcher rather than re-deriving it from the tables
 * underneath and drifting away from the printed sheet.
 */
export function useIcsFormInputs(
  formname: string,
  options?: {
    /** Extra payload the fetcher needs, e.g. `{ requestId }` for ICS-213RR. */
    data?: Record<string, unknown>;
    periodNumber?: number;
    enabled?: boolean;
  }
) {
  const { data: incident } = useIncident();
  const user = useAppSelector(selectUser);
  const groupGuid = useAppSelector(selectGroupGuid);

  return useQuery({
    queryKey: icsFormInputsKeys.one(
      incident?.id,
      formname,
      options?.periodNumber,
      options?.data
    ),
    enabled: Boolean(incident?.id) && options?.enabled !== false,
    queryFn: async (): Promise<IcsFormInputs> => {
      const response = await axios.post<FormInputsResponse>(
        config.apiGateway.icsFormInputs,
        {
          formname,
          data: options?.data ?? {},
          user,
          incident,
          group_guid: groupGuid,
          periodNumber: options?.periodNumber ?? null,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }
      );
      return response.data?.inputs ?? {};
    },
  });
}
